<template>
    <a-modal
        :open="isModalVisible"
        :title="modalTitle"
        width="670px"
        centered
        :confirm-loading="saveLoading"
        :mask-closable="false"
        :keyboard="false"
        @cancel="handleCancel"
        :footer="null"
        class="user-add-modal"
        :body-style="{ display: 'flex', flexDirection: 'column', padding: '0', maxHeight: 'calc(75vh - 170px)' }"
    >
        <div class="modal-scroll-content">
            <a-form ref="formRef" :model="formState" layout="vertical" class="user-add-form">
                <a-row :gutter="16">
                    <a-col :span="12">
                        <a-form-item label="所属组织" name="role" :rules="[{ required: true, message: '请输入所属组织' }]">
                            <a-input v-model:value="formState.role" placeholder="例如：技术部" allow-clear>
                                <template #prefix><ApartmentOutlined /></template>
                            </a-input>
                        </a-form-item>
                    </a-col>
                    <a-col :span="12">
                        <a-form-item label="员工姓名" name="name" :rules="[{ required: true, message: '请输入员工姓名' }]">
                             <a-input v-model:value="formState.name" placeholder="请输入姓名" allow-clear>
                                 <template #prefix><UserOutlined /></template>
                             </a-input>
                        </a-form-item>
                    </a-col>
                </a-row>

                <a-divider>声纹采集</a-divider>

                <div class="voice-recording-section">
                    <!-- Text to Read (Visible when idle) -->
                    <div v-if="recordingState === 'idle' || recordingState === 'error'" class="read-content-box">
                        <p class="read-tip">
                            <InfoCircleOutlined /> 点击"开始录音"按钮后，请清晰、匀速地朗读以下文字：
                        </p>
                        <div class="read-text">
                            "大家好，我是今天的主持人，今天的会议即将开始，请大家提前 10分钟到达会议室做准备。"
                        </div>
                    </div>

                     <!-- Recording Controls / Status -->
                     <div class="recording-controls">
                         <!-- Waveform (Visible when recording) -->
                         <div v-show="showWaveform" class="waveform-container" ref="recwaveRef">
                             <!-- Waveform will be rendered here -->
                             <span v-if="!waveVisualizer" class="waveform-placeholder">正在初始化波形...</span>
                         </div>

                         <!-- Player (Visible when recorded or editing existing) -->
                          <div v-if="showPlayer" class="player-container">
                               <span class="player-label"><CustomerServiceOutlined /> 录音试听:</span>
                               <HszdAudio ref="audioPlayerRef" :source="audioSource" />
                          </div>

                          <!-- Status Text (Optional: Loading/Error) -->
                          <div v-if="recordingState === 'loading'" class="status-text"><a-spin size="small" /> 正在加载声纹信息...</div>
                          <div v-if="recordingState === 'initializing'" class="status-text"><a-spin size="small" /> 正在准备录音设备...</div>
                          <div v-if="recordingState === 'processing'" class="status-text"><a-spin size="small" /> 正在处理录音...</div>
                           <div v-if="recordingState === 'error'" class="status-text error-text"><ExclamationCircleOutlined /> 处理出错，请重试。</div>


                         <!-- Action Buttons -->
                         <a-space class="action-buttons" :size="12">
                             <a-button
                                 type="primary"
                                 @click="startRecordingProcess"
                                 :disabled="!canStartRecording"
                                 :loading="recordingState === 'initializing'"
                             >
                                 <template #icon><AudioOutlined /></template>
                                 开始录音
                             </a-button>
                             <a-button
                                 type="primary"
                                 danger
                                 @click="stopRecordingProcess"
                                 :disabled="!canStopRecording"
                                 :loading="recordingState === 'processing'"
                             >
                                  <template #icon><StopOutlined /></template>
                                 结束录音
                             </a-button>
                             <a-button @click="resetRecording" :disabled="recordingState === 'recording' || recordingState === 'uploading' || recordingState === 'initializing' || recordingState === 'processing'">
                                 <template #icon><RedoOutlined /></template>
                                 {{ (recordedBlob || editModeHasExistingAudio) ? '重新录制' : '重置' }}
                             </a-button>
                         </a-space>
                     </div>
                </div>
            </a-form>

            <!-- Custom Footer MOVED INSIDE scroll content -->
            <div class="modal-footer">
                <a-space>
                    <a-button @click="handleCancel">取消</a-button>
                    <a-button
                        type="primary"
                        @click="handleSave"
                        :loading="saveLoading"
                        :disabled="saveLoading ||
                                   recordingState === 'recording' ||
                                   recordingState === 'processing' ||
                                   recordingState === 'initializing' ||
                                   recordingState === 'uploading' ||
                                   (!props.formId && !recordedBlob)"
                    >
                        <template #icon><SaveOutlined /></template>
                        保存声纹
                    </a-button>
                </a-space>
            </div>
        </div>
    </a-modal>
</template>

<script setup>
import { ref, reactive, watch, computed, nextTick, onUnmounted } from 'vue'; // Added onUnmounted
import axios from 'axios';
import { message } from 'ant-design-vue';
import {
    ApartmentOutlined, // Icon for Organization
    UserOutlined,      // Icon for User
    AudioOutlined,     // Icon for Start Recording
    StopOutlined,      // Icon for Stop Recording
    RedoOutlined,      // Icon for Redo/Reset
    SaveOutlined,      // Icon for Save
    ExclamationCircleOutlined, // For confirmations (if needed later)
    CustomerServiceOutlined, // Icon for Playback area
    InfoCircleOutlined // Icon for tip
} from '@ant-design/icons-vue'; // Import necessary icons
import Recorder from 'recorder-core';
import 'recorder-core/src/engine/mp3';
import 'recorder-core/src/engine/mp3-engine';
import 'recorder-core/src/engine/wav';
import 'recorder-core/src/extensions/waveview';
import { BASE_URL } from '@/config/api';
import HszdAudio from '@/components/hszdAudio/HszdAudio.vue';

// --- Props and Emits ---
const props = defineProps({
    formId: { type: [Number, String], default: null }, // Allow string if ID can be non-numeric
    open: { type: Boolean, default: false }
});
const emits = defineEmits(['save-success', 'update:open']);

// --- Refs ---
const formRef = ref();
const recwaveRef = ref(null); // Renamed from recwave
const audioPlayerRef = ref(); // Renamed from audioRef
const isModalVisible = computed({ // Use computed for v-model binding on modal
    get: () => props.open,
    set: (value) => emits('update:open', value)
});

// --- State ---
const formState = reactive({ name: '', role: '' });
const recordingState = ref('idle'); // idle, initializing, recording, processing, recorded, uploading, error, loading
const audioSource = ref(''); // URL for playback
const recordedBlob = ref(null); // Store the recorded blob
const saveLoading = ref(false);
const recorderInstance = ref(null); // Store recorder instance
const waveVisualizer = ref(null); // Store wave visualizer instance
const editModeHasExistingAudio = ref(false); // Track if editing user already had audio

// --- Computed Properties ---
const modalTitle = computed(() => props.formId ? '编辑声纹' : '新建声纹');
const canStartRecording = computed(() => recordingState.value === 'idle' || recordingState.value === 'error' || recordingState.value === 'recorded'); // Allow starting if idle, error or already recorded (implies redo)
const canStopRecording = computed(() => recordingState.value === 'recording');
const showWaveform = computed(() => recordingState.value === 'recording');
const showPlayer = computed(() => (recordingState.value === 'recorded' && audioSource.value) || (props.formId && editModeHasExistingAudio.value && audioSource.value && recordingState.value !== 'recording')); // Show if recorded OR editing existing (and not currently re-recording)

// --- Functions ---

const getErrorMessage = (error, fallback) => {
    const data = error?.response?.data;
    if (typeof data === 'string' && data.trim()) {
        return data;
    }
    return data?.message || data?.detail || error?.message || fallback;
};

// Reset all recording related states
const resetRecording = (keepFormState = false) => {
    console.log('Executing resetRecording...');
    if (recorderInstance.value) {
        try {
            recorderInstance.value.close();
            console.log('Recorder instance closed.');
        } catch (e) { console.warn("Error closing recorder:", e); }
        recorderInstance.value = null;
    }
    if (waveVisualizer.value) {
         try {
            waveVisualizer.value.destroy?.();
            console.log('Wave visualizer destroyed.');
         } catch(e) { console.warn("Error destroying wave visualizer:", e); }
         waveVisualizer.value = null;
    }
    if (audioSource.value && audioSource.value.startsWith('blob:')) {
        URL.revokeObjectURL(audioSource.value);
        console.log('Blob URL revoked:', audioSource.value);
    }
    audioSource.value = '';
    recordedBlob.value = null;
    recordingState.value = 'idle';
    saveLoading.value = false;
    if (!keepFormState) {
        formRef.value?.resetFields();
        console.log('Form fields reset.');
    }
    editModeHasExistingAudio.value = false; // Reset this flag too
    console.log('Recording reset complete. State:', recordingState.value);
};

// Fetch user data for editing
const fetchUser = async (id) => {
    resetRecording(true); // Reset recording state but keep form potentially
    recordingState.value = 'loading'; // Indicate loading state
    console.log('Fetching user data for ID:', id);
    try {
        const response = await axios.get(`${BASE_URL}/user/${id}`);
        console.log('Fetch user response:', response.data);
        if (response.data) { // Adjust based on your API response structure
            formState.name = response.data.name;
            formState.role = response.data.role;
            if (response.data.audio_path) {
                console.log('Existing audio path found:', response.data.audio_path);
                // Use a cache-busting query param? Maybe not needed if URL changes on update
                audioSource.value = response.data.audio_path + `?t=${new Date().getTime()}`; // Add timestamp to try cache busting
                editModeHasExistingAudio.value = true;
                recordingState.value = 'recorded'; // Set state as recorded (but no blob for re-upload yet)
                console.log('State set to recorded (edit mode with audio).');
            } else {
                 console.log('No existing audio path found.');
                 editModeHasExistingAudio.value = false;
                 recordingState.value = 'idle'; // No existing audio, ready to record
                 console.log('State set to idle (edit mode without audio).');
            }
        } else {
             message.error('未找到用户信息');
             recordingState.value = 'error';
        }
    } catch (error) {
        console.error('获取用户信息失败:', error);
        message.error( error.response?.data?.message || '获取用户信息失败');
        recordingState.value = 'error';
    } finally {
         if (recordingState.value === 'loading') {
             // If still loading (e.g., fetch failed early), set to error or idle
             recordingState.value = 'error';
         }
    }
};

// Start the recording process
const startRecordingProcess = () => {
    // Reset previous recording if exists, keep form state
    resetRecording(true);
    recordingState.value = 'initializing'; // Indicate initialization
    console.log('Starting recording process...');

    const newRec = Recorder({
        type: 'mp3',
        sampleRate: 16000,
        bitRate: 16,
        onProcess: (buffers, powerLevel, bufferSampleRate) => {
             // Debounce or throttle waveform input if performance is an issue
             if (waveVisualizer.value) {
                waveVisualizer.value.input(buffers[buffers.length - 1], powerLevel, bufferSampleRate);
            }
        },
    });

    newRec.open(
        () => {
            console.log('录音权限已获取');
            recorderInstance.value = newRec; // Store instance

            // Set state to 'recording' FIRST to make the container potentially visible
            recordingState.value = 'recording';
            message.success('录音已开始'); // Give feedback slightly earlier
            console.log('Recording state set to recording.');

            // Now use nextTick to ensure the container is rendered and has dimensions
            nextTick(() => {
                if (recwaveRef.value && !waveVisualizer.value) { // Check again in nextTick
                    const containerWidth = recwaveRef.value.offsetWidth;
                    console.log('Waveform container width in nextTick:', containerWidth);
                    if (containerWidth > 0) { // Only initialize if width is valid
                         try {
                            waveVisualizer.value = Recorder.WaveView({
                                elem: recwaveRef.value,
                                height: 60, // Keep fixed height
                                width: containerWidth // Use measured width
                            });
                            console.log('Wave visualizer created.');
                         } catch (e) {
                             console.error("Error creating WaveView:", e);
                             message.error("无法初始化波形显示");
                             // Log error but proceed
                         }
                    } else {
                         console.warn("Waveform container width is 0 in nextTick. Cannot initialize WaveView.");
                         // Proceed without wave view if width is 0
                    }
                }
                // Start recording regardless of WaveView init success/failure
                // Ensure instance still exists (might be closed in edge cases?)
                if (recorderInstance.value) {
                    recorderInstance.value.start();
                    console.log('Recording actually started (inside nextTick).');
                } else {
                    console.warn('Recorder instance was null when trying to start inside nextTick.');
                    // Handle this unexpected state? Maybe revert recordingState?
                     recordingState.value = 'error';
                     message.error("录音启动失败，请重试。");
                     resetRecording(true);
                }
            });
        },
        (msg, isUserNotAllow) => {
            console.error((isUserNotAllow ? 'UserNotAllow，' : '') + '无法录音: ' + msg);
            message.error('无法启动录音: ' + msg);
            recordingState.value = 'error';
            resetRecording(true); // Clean up if open failed, keep form
        }
    );
};

// Stop the recording
const stopRecordingProcess = () => {
    if (!recorderInstance.value || recordingState.value !== 'recording') {
        console.warn('Recorder not active or not in recording state.');
        return;
    }
    recordingState.value = 'processing'; // Indicate processing state
    message.loading({ content: '正在处理录音...', key: 'processing' }); // Use key for specific message control
    console.log('Stopping recording...');

    recorderInstance.value.stop(
        (blob, duration) => {
            message.success({ content: `录音完成，时长 ${Math.round(duration / 1000)} 秒`, key: 'processing', duration: 2 });
            console.log('录音成功', blob, '时长:', duration, 'ms');
            recordedBlob.value = blob;
            // Revoke previous blob URL if exists
            if (audioSource.value && audioSource.value.startsWith('blob:')) {
                URL.revokeObjectURL(audioSource.value);
            }
            audioSource.value = URL.createObjectURL(blob); // Create object URL for playback
            recordingState.value = 'recorded';
            console.log('State set to recorded. Blob URL:', audioSource.value);

            // Ensure recorder resources are released after stopping
             nextTick(() => { // Delay close slightly?
                if(recorderInstance.value) {
                     try {
                        recorderInstance.value.close();
                        recorderInstance.value = null;
                        console.log('Recorder instance closed after stop.');
                    } catch(e) {console.warn("Error closing recorder after stop:", e);}
                }
             });
        },
        (err) => {
            message.error({ content: '结束录音失败: ' + err, key: 'processing', duration: 2 });
            console.error('结束录音出错：', err);
            recordingState.value = 'error';
            resetRecording(true); // Clean up on error, keep form
        }
    );
};

// Handle Save/Upload
const handleSave = async () => {
    // Check if we have a new recording OR if we are editing an existing user (even without a new recording)
    // This initial check might be too complex or slightly off, let's simplify below.
    /*
    if (recordingState.value !== 'recorded' && !(props.formId && editModeHasExistingAudio.value)) {
         // If it's a new user, state MUST be 'recorded'
         if (!props.formId) {
             message.warn('请先完成录音');
             return;
         }
         // If editing existing, and state is not 'recorded', it means no new recording was made.
         // Allow saving name/role changes without a new blob.
    }
    */

    // --- Revised Logic Start ---

    // 1. Validate form first
    try {
        await formRef.value.validate();
    } catch (validationError) {
        console.log('表单验证失败:', validationError);
        // Form validation usually shows messages automatically
        return;
    }

    const isNewUser = !props.formId;
    const hasNewRecording = !!recordedBlob.value; // Check if a new blob exists

    // 2. Check if recording is required but missing (only for new users)
    if (isNewUser && !hasNewRecording) {
        message.warn('新建用户必须录制声纹');
        return;
    }

    // 3. Remove the faulty check that caused the error:
    // This check incorrectly flagged editing existing audio (where state is 'recorded' but blob is null) as an error.
    /*
    if (recordingState.value === 'recorded' && !recordedBlob.value) {
        console.error("State is 'recorded' but recordedBlob is null. This shouldn't happen.");
        message.error("录音数据丢失，请重试");
        recordingState.value = 'error';
        return;
    }
    */

     // This check below is also redundant because of the !isNewUser || hasNewRecording check above.
     /*
     if (!props.formId && !recordedBlob.value) {
         message.warn('请先完成录音');
         return;
     }
     */

    // 4. Proceed with saving logic
    saveLoading.value = true;
    recordingState.value = 'uploading';
    console.log('Starting save/upload...');

    const formData = new FormData();
    formData.append('name', formState.name);
    formData.append('role', formState.role);

    // Only append the file blob if it exists (a new recording was made)
    if (hasNewRecording) {
        formData.append('file', recordedBlob.value, `${formState.name || 'recorder'}.mp3`);
        console.log('New audio blob appended to form data.');
    } else {
        console.log('No new audio blob to upload (likely editing name/role only).');
    }

    // Determine URL and Method
    const url = props.formId ? `${BASE_URL}/user/${props.formId}` : `${BASE_URL}/user`;
    const method = props.formId ? 'PUT' : 'POST';
    console.log(`Submitting to URL: ${url} with method: ${method}`);

    // Add active_id if needed by backend
    const myPermission = JSON.parse(localStorage.getItem('myPermission') || '{}');
    if (myPermission.id) {
        formData.append('active_id', myPermission.id);
    }

    try {
        const response = await axios({ method, url, data: formData });
        console.log('Save response:', response);
        const bodyCode = response.data?.code;
        if (response.status >= 200 && response.status < 300 && (bodyCode === undefined || bodyCode === 200)) {
            message.success('声纹保存成功');
            emits('save-success');
            handleCancel(); // Close modal on success
        } else {
            const errorMsg = response.data?.message || response.data?.detail || `保存失败 (Status: ${response.status})`;
            message.error(errorMsg);
            // Revert state based on whether we had a new blob or existing audio
             recordingState.value = hasNewRecording ? 'recorded' : (editModeHasExistingAudio.value ? 'recorded' : 'idle');
        }
    } catch (error) {
        console.error('保存请求失败:', error);
        const errorMsg = getErrorMessage(error, '保存请求失败，请检查网络或联系管理员');
        message.error(errorMsg);
        // Revert state more carefully based on whether we had a new blob or existing audio
         recordingState.value = hasNewRecording ? 'recorded' : (editModeHasExistingAudio.value ? 'recorded' : 'error');
    } finally {
        saveLoading.value = false;
        // Ensure state is not stuck in 'uploading' if save fails
        if (recordingState.value === 'uploading') {
             // Revert based on whether there's a blob (new or redo) or existing audio
             if (hasNewRecording) {
                 recordingState.value = 'recorded';
             } else if (editModeHasExistingAudio.value) {
                 recordingState.value = 'recorded'; // Revert to recorded state (showing existing)
             } else {
                 // This case (uploading without blob and no existing) shouldn't be reachable
                 recordingState.value = 'error';
             }
            console.log('Save finished (failed or succeeded), state reverted to:', recordingState.value);
        }
    }
    // --- Revised Logic End ---
};

// Handle Cancel / Close
const handleCancel = () => {
    console.log('Modal cancel triggered.');
    resetRecording(); // Full reset on cancel
    emits('update:open', false);
};

// --- Watchers ---
watch(() => props.open, (newOpen, oldOpen) => {
     console.log(`Modal open state changed: ${oldOpen} -> ${newOpen}`);
    if (newOpen) {
        // Reset state completely first when modal opens
        resetRecording();
        editModeHasExistingAudio.value = false;

        if (props.formId) {
            console.log('Modal opened in edit mode for ID:', props.formId);
            fetchUser(props.formId);
        } else {
            console.log('Modal opened in create mode.');
            // Ensure form fields are reset for 'new' mode
            nextTick(() => { // Use nextTick to ensure formRef is available
                formRef.value?.resetFields();
                 recordingState.value = 'idle';
                 console.log('New mode: Form reset, state set to idle.');
            });
        }
    } else {
         // Optional: Reset state when modal is closed externally? resetRecording() called in handleCancel already.
         // resetRecording(); // Maybe redundant if handleCancel always fires
    }
});

// --- Lifecycle ---
onUnmounted(() => {
     console.log("UserAdd unmounted, cleaning up recorder and revoking URL...");
     resetRecording(); // Ensure resources are freed when component is destroyed
});

</script>

<style scoped lang="less">
.user-add-modal {
    :deep(.ant-modal-content) {
        border-radius: 8px;
    }
    :deep(.ant-modal-header) {
        border-radius: 8px 8px 0 0;
        border-bottom: 1px solid #f0f0f0;
        margin-bottom: 0;
    }
     :deep(.ant-modal-body) {
        padding: 20px 24px;
        overflow-y: auto;
    }
}

.modal-scroll-content {
}

.user-add-form {
    // Adjust form padding/margins if needed
}

.voice-recording-section {
    margin-top: 16px;
    padding: 16px;
    background-color: #f9fafb; // Lighter background for this section
    border-radius: 8px;
    border: 1px solid #e5e7eb;
}

.read-content-box {
    margin-bottom: 16px;
    padding: 12px;
    background-color: #fff;
    border-radius: 6px;
    border: 1px solid #e5e7eb;

    .read-tip {
        font-size: 13px;
        color: #6b7280;
        margin-bottom: 8px;
        display: flex;
        align-items: center;
        .anticon {
            margin-right: 6px;
            color: #1890ff;
        }
    }

    .read-text {
        font-size: 14px;
        line-height: 1.6;
        color: #374151;
        background-color: #f0f2f5; // Slightly different background for text itself
        padding: 8px 12px;
        border-radius: 4px;
        font-style: italic;
    }
}

.recording-controls {
    display: flex;
    flex-direction: column;
    gap: 12px; // Reduced gap

    .waveform-container {
        width: 100%;
        height: 60px;
        background-color: #fff;
        border-radius: 4px;
        border: 1px solid #d9d9d9;
        display: flex;
        align-items: center;
        justify-content: center;
        overflow: hidden; // Ensure waveform canvas fits
        position: relative; // For placeholder positioning

        .waveform-placeholder {
             color: #bfbfbf;
             font-size: 12px;
             position: absolute; // Center placeholder
             top: 50%;
             left: 50%;
             transform: translate(-50%, -50%);
        }
        :deep(canvas) {
             display: block; // Ensure canvas behaves predictably
             width: 100% !important;
             height: 100% !important;
        }
    }

    .player-container {
         width: 100%;
         display: flex;
         align-items: center;
         gap: 10px;
         padding: 8px;
         background: #f0f2f5;
         border-radius: 4px;

        .player-label {
             font-size: 13px;
             color: #595959;
             flex-shrink: 0; // Prevent label from shrinking
             .anticon {
                 margin-right: 4px;
                 vertical-align: middle; // Align icon better
             }
         }
         // Target HszdAudio internals if needed
         :deep(.hszd-audio-player-wrapper) { // Adjust selector based on HszdAudio's structure
             flex-grow: 1;
             // Add specific styles if HszdAudio doesn't fit well
         }
    }

     .status-text {
         font-size: 13px;
         color: #8c8c8c;
         display: flex;
         align-items: center;
         gap: 6px;
         min-height: 22px; // Reserve space even when empty
         &.error-text {
             color: #ff4d4f;
         }
     }


    .action-buttons {
        /* margin-top: 8px; */ // Removed margin-top
    }
}


.modal-footer {
    border-top: 1px solid #f0f0f0;
    padding: 16px 0 0 0; // Adjust padding, maybe only top padding needed
    margin-top: 24px; // Add margin to separate from form content
    background: #fff; // Keep background or remove?
    text-align: right;
}

/* Remove or adjust original styles if they conflict */
.radio-form { // Keep or remove? Seems unused now.
    // ...
}

/* Remove styles for elements replaced by a-space or new layout */
.btn-groups,
.option-btns,
.info-btn {
    display: none; // Hide old button groups explicitly if they weren't removed from template (they were)
}

</style>
